import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "@/context/AuthContext";
import { logger } from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShowcaseGallery() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/showcase`, { params: { limit: 12 } });
        if (alive) setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        logger.warn("Failed to fetch showcase:", e);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!loaded || items.length === 0) return null;

  return (
    <section
      id="our-work-gallery"
      data-testid="showcase-section"
      className="py-24 md:py-32 bg-stone-50 scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">
              Our work
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 leading-tight" style={{ fontFamily: "Outfit" }}>
              Real homes. Real transformations.
            </h2>
          </div>
          <p className="md:max-w-sm text-stone-600">
            Hover or tap any photo to slide between before and after. Every project, straight
            from a Goodlettsville neighbor.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="showcase-grid">
          {items.map((it, idx) => (
            <ShowcaseCard key={it.id} item={it} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ShowcaseCard({ item, idx }) {
  const beforeArr = item.before_photos || [];
  const afterArr = item.after_photos || [];
  const [bIdx, setBIdx] = useState(0);
  const [aIdx, setAIdx] = useState(0);
  const [showAfter, setShowAfter] = useState(false);

  const before = beforeArr[bIdx];
  const after = afterArr[aIdx];

  // If no before photo, just show after only
  const hasBefore = !!before;

  return (
    <div
      data-testid={`showcase-card-${idx}`}
      className="group relative rounded-3xl overflow-hidden bg-white shadow-md hover:shadow-2xl transition-all duration-500 border border-stone-100"
      onMouseEnter={() => hasBefore && setShowAfter(true)}
      onMouseLeave={() => hasBefore && setShowAfter(false)}
      onClick={() => hasBefore && setShowAfter((s) => !s)}
    >
      <div className="relative aspect-[4/5] overflow-hidden cursor-pointer">
        {hasBefore && (
          <img
            src={`${BACKEND_URL}${before.url}`}
            alt={`Before — ${item.title}`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        )}
        {after && (
          <img
            src={`${BACKEND_URL}${after.url}`}
            alt={`After — ${item.title}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
              hasBefore ? (showAfter ? "opacity-100" : "opacity-0") : "opacity-100"
            }`}
            loading="lazy"
          />
        )}
        {hasBefore && (
          <div className="absolute top-4 left-4">
            <span
              className={`inline-block text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full transition-colors ${
                showAfter ? "bg-brand-500 text-white" : "bg-stone-900/85 text-white"
              }`}
            >
              {showAfter ? "After" : "Before"}
            </span>
          </div>
        )}

        {/* photo paginator if multiple photos */}
        {((showAfter && afterArr.length > 1) || (!showAfter && hasBefore && beforeArr.length > 1)) && (
          <div className="absolute top-4 right-4 flex items-center gap-1 bg-white/85 backdrop-blur rounded-full p-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (showAfter) setAIdx((aIdx - 1 + afterArr.length) % afterArr.length);
                else setBIdx((bIdx - 1 + beforeArr.length) % beforeArr.length);
              }}
              className="w-6 h-6 rounded-full hover:bg-stone-100 flex items-center justify-center"
              aria-label="Previous photo"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="text-[10px] font-bold text-stone-700 px-1">
              {(showAfter ? aIdx : bIdx) + 1}/{showAfter ? afterArr.length : beforeArr.length}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (showAfter) setAIdx((aIdx + 1) % afterArr.length);
                else setBIdx((bIdx + 1) % beforeArr.length);
              }}
              className="w-6 h-6 rounded-full hover:bg-stone-100 flex items-center justify-center"
              aria-label="Next photo"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-white font-bold text-lg truncate" style={{ fontFamily: "Outfit" }}>
                {item.title}
              </div>
              <div className="text-white/75 text-sm truncate">
                {item.city || item.service}
              </div>
            </div>
            {item.rating ? (
              <div className="flex items-center gap-0.5 flex-shrink-0">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`w-3.5 h-3.5 ${
                      n <= item.rating ? "text-brand-300 fill-brand-300" : "text-white/30"
                    }`}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
